import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { FeatureVersion, ResolveFeatureResponse } from '@/src/types/featureExperience'
import * as featureExperienceApi from '@/src/services/http/featureExperience'

export type FeatureExperienceStatus = 'idle' | 'loading' | 'succeeded' | 'failed'

export interface FeatureExperienceState {
  resolvedFeatures: ResolveFeatureResponse['features']
  status: FeatureExperienceStatus
  error: string | null
  requestedAt: string | null
  dismissedBanners: string[]
  setPreferencePending: boolean
}

const initialState: FeatureExperienceState = {
  resolvedFeatures: {},
  status: 'idle',
  error: null,
  requestedAt: null,
  dismissedBanners: [],
  setPreferencePending: false
}

export const resolveFeatures = createAsyncThunk<
  ResolveFeatureResponse,
  { keys: string[]; restaurantId?: string | number | null }
>('featureExperience/resolve', async ({ keys, restaurantId }) => {
  return featureExperienceApi.resolveFeatures(keys, restaurantId)
})

export const setFeaturePreference = createAsyncThunk<
  ResolveFeatureResponse,
  {
    featureKey: string
    preferredVersion: FeatureVersion
    restaurantId?: string | number | null
  }
>('featureExperience/setPreference', async (params, { dispatch }) => {
  await featureExperienceApi.setPreference(params.featureKey, params.preferredVersion, params.restaurantId)
  const result = await dispatch(
    resolveFeatures({ keys: [params.featureKey], restaurantId: params.restaurantId })
  ).unwrap()

  return result
})

const featureExperienceSlice = createSlice({
  name: 'featureExperience',
  initialState,
  reducers: {
    resetFeatureExperience: () => initialState,
    dismissCutoverBanner: (state, action: PayloadAction<string>) => {
      const key = action.payload
      if (!state.dismissedBanners.includes(key)) {
        state.dismissedBanners.push(key)
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(resolveFeatures.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(resolveFeatures.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.resolvedFeatures = {
          ...state.resolvedFeatures,
          ...action.payload.features
        }
        state.requestedAt = action.payload.requestedAt
      })
      .addCase(resolveFeatures.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message ?? 'Failed to resolve features'
      })
      .addCase(setFeaturePreference.pending, state => {
        state.setPreferencePending = true
        state.error = null
      })
      .addCase(setFeaturePreference.fulfilled, state => {
        state.setPreferencePending = false
      })
      .addCase(setFeaturePreference.rejected, (state, action) => {
        state.setPreferencePending = false
        state.error = action.error.message ?? 'Failed to update preference'
      })
  }
})

export const { resetFeatureExperience, dismissCutoverBanner } = featureExperienceSlice.actions

/** Slice-shaped state; use with full RootState from the store in useSelector. */
export type FeatureExperienceRoot = { featureExperience: FeatureExperienceState }

export const selectResolvedFeature = (featureKey: string) => (state: FeatureExperienceRoot) =>
  state.featureExperience.resolvedFeatures[featureKey]

export const selectFeatureExperienceStatus = (state: FeatureExperienceRoot) => state.featureExperience.status

export default featureExperienceSlice.reducer
